import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { hipaaAPI } from '../../lib/api';
import { 
  ShieldCheck, 
  FileText, 
  Check,
  X,
  Warning,
  PencilSimple,
  Eye,
  Clock,
  CheckCircle
} from '@phosphor-icons/react';

export default function HIPAAConsentPage() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [consents, setConsents] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [viewConsent, setViewConsent] = useState(null);
  const [signature, setSignature] = useState('');
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [templatesRes, consentsRes, statusRes] = await Promise.all([
        hipaaAPI.getTemplates(),
        hipaaAPI.getMyConsents(),
        hipaaAPI.getStatus(),
      ]);
      setTemplates(templatesRes.data);
      setConsents(consentsRes.data);
      setStatus(statusRes.data);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading HIPAA data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignConsent = async (e) => {
    e.preventDefault();
    if (!signature.trim() || !agreed) return;
    
    setSigning(true);
    try {
      await hipaaAPI.signConsent({
        consent_type: selectedTemplate.type,
        signature: signature.trim(),
      });
      setSelectedTemplate(null);
      setSignature('');
      setAgreed(false);
      loadData();
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error signing consent:', error);
    } finally {
      setSigning(false);
    }
  };

  const getConsentIcon = (type) => {
    switch (type) {
      case 'privacy_notice': return ShieldCheck;
      case 'treatment_consent': return FileText;
      case 'release_info': return Eye;
      case 'telehealth_consent': return FileText;
      case 'financial_responsibility': return FileText;
      case 'medication_records_release': return FileText;
      case 'medical_records_release': return FileText;
      default: return FileText;
    }
  };

  const isConsentSigned = (type) => {
    return consents.some(c => c.consent_type === type && c.is_active);
  };

  const getSignedConsent = (type) => {
    return consents.find(c => c.consent_type === type && c.is_active);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
          HIPAA Consent Forms
        </h1>
        <p className="text-slate-500 mt-1">Review and sign required healthcare consent forms</p>
      </div>

      {/* Expiration Warning Banner */}
      {status && (status.any_expired || status.any_expiring_soon) && (
        <div className={`mb-8 p-4 rounded-lg flex items-start gap-3 ${
          status.any_expired ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'
        }`} data-testid="consent-expiration-banner">
          <Warning size={24} className={status.any_expired ? 'text-red-600' : 'text-amber-600'} />
          <div>
            <h3 className={`font-semibold ${status.any_expired ? 'text-red-800' : 'text-amber-800'}`}>
              {status.any_expired ? 'Consent(s) Expired' : 'Consent(s) Expiring Soon'}
            </h3>
            <p className={`text-sm mt-1 ${status.any_expired ? 'text-red-700' : 'text-amber-700'}`}>
              {status.any_expired 
                ? 'One or more of your HIPAA consents have expired and need to be renewed. Please re-sign the affected forms below.'
                : 'One or more of your HIPAA consents will expire within the next 30 days. Consider renewing them now to avoid interruption in services.'}
            </p>
          </div>
        </div>
      )}

      {/* Status Banner */}
      {status && !status.all_required_signed && (
        <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3" data-testid="consent-warning-banner">
          <Warning size={24} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-800">Action Required</h3>
            <p className="text-amber-700 text-sm mt-1">
              Please review and sign all required consent forms to complete your patient registration. 
              These forms are necessary for us to provide you with healthcare services.
            </p>
          </div>
        </div>
      )}

      {status && status.all_required_signed && (
        <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3" data-testid="consent-complete-banner">
          <CheckCircle size={24} className="text-green-600 flex-shrink-0 mt-0.5" weight="fill" />
          <div>
            <h3 className="font-semibold text-green-800">All Required Consents Signed</h3>
            <p className="text-green-700 text-sm mt-1">
              Thank you! You have signed all required HIPAA consent forms. You can review or update your consents at any time.
            </p>
          </div>
        </div>
      )}

      {/* Consent Forms Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => {
          const Icon = getConsentIcon(template.type);
          const signed = isConsentSigned(template.type);
          const signedConsent = getSignedConsent(template.type);
          
          return (
            <div 
              key={template.type} 
              className={`card-clinical p-6 ${!signed && template.required ? 'border-amber-300 bg-amber-50/30' : ''}`}
              data-testid={`consent-card-${template.type}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  signed ? 'bg-green-100' : template.required ? 'bg-amber-100' : 'bg-slate-100'
                }`}>
                  <Icon size={24} className={
                    signed ? 'text-green-600' : template.required ? 'text-amber-600' : 'text-slate-500'
                  } />
                </div>
                {signed ? (
                  <span className="status-badge status-completed flex items-center gap-1">
                    <Check size={12} weight="bold" /> Signed
                  </span>
                ) : template.required ? (
                  <span className="status-badge status-pending">Required</span>
                ) : (
                  <span className="status-badge bg-slate-100 text-slate-600">Optional</span>
                )}
              </div>
              
              <h3 className="font-semibold text-slate-900 mb-2">{template.title}</h3>
              
              {signed && signedConsent && (
                <div className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                  <Clock size={12} />
                  Signed on {new Date(signedConsent.signature_date).toLocaleDateString()}
                </div>
              )}
              
              {/* Show expiration date from status */}
              {signed && status && (
                <div className={`text-xs mb-4 flex items-center gap-1 ${
                  status.any_expired && !status[template.type] ? 'text-red-600' : 
                  status.any_expiring_soon ? 'text-amber-600' : 'text-green-600'
                }`}>
                  {template.type === 'privacy_notice' && status.privacy_notice_expires && (
                    <>
                      <ShieldCheck size={12} />
                      Expires {new Date(status.privacy_notice_expires).toLocaleDateString()}
                    </>
                  )}
                  {template.type === 'treatment_consent' && status.treatment_consent_expires && (
                    <>
                      <ShieldCheck size={12} />
                      Expires {new Date(status.treatment_consent_expires).toLocaleDateString()}
                    </>
                  )}
                  {template.type === 'financial_responsibility' && status.financial_responsibility_expires && (
                    <>
                      <ShieldCheck size={12} />
                      Expires {new Date(status.financial_responsibility_expires).toLocaleDateString()}
                    </>
                  )}
                </div>
              )}
              
              <div className="flex gap-2 mt-4">
                {signed ? (
                  <>
                    <button
                      onClick={() => setViewConsent(signedConsent)}
                      className="flex-1 btn-outline text-sm py-2 flex items-center justify-center gap-1"
                      data-testid={`view-consent-${template.type}`}
                    >
                      <Eye size={16} /> View
                    </button>
                    <button
                      onClick={() => setSelectedTemplate(template)}
                      className="flex-1 btn-outline text-sm py-2 flex items-center justify-center gap-1"
                      data-testid={`resign-consent-${template.type}`}
                    >
                      <PencilSimple size={16} /> Re-sign
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setSelectedTemplate(template)}
                    className="w-full btn-teal text-sm py-2"
                    data-testid={`sign-consent-${template.type}`}
                  >
                    Review & Sign
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Sign Consent Modal */}
      {selectedTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] flex flex-col" data-testid="sign-consent-modal">
            <div className="p-6 border-b border-slate-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">{selectedTemplate.title}</h2>
                  <p className="text-sm text-slate-500 mt-1">Please read carefully before signing</p>
                </div>
                <button 
                  onClick={() => {
                    setSelectedTemplate(null);
                    setSignature('');
                    setAgreed(false);
                  }} 
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="prose prose-sm max-w-none">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {selectedTemplate.text}
                </div>
              </div>
            </div>
            
            <form onSubmit={handleSignConsent} className="p-6 border-t border-slate-200 flex-shrink-0 space-y-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="agree-checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-1 w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500"
                  data-testid="consent-agree-checkbox"
                />
                <label htmlFor="agree-checkbox" className="text-sm text-slate-700">
                  I have read, understand, and agree to the terms outlined in this document. 
                  I understand that my electronic signature below has the same legal effect as a handwritten signature.
                </label>
              </div>
              
              <div>
                <label className="label-clinical">Electronic Signature (Type your full legal name)</label>
                <input
                  type="text"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder={`${user?.first_name} ${user?.last_name}`}
                  className="input-clinical font-semibold"
                  data-testid="consent-signature-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  By typing your name, you are signing this document electronically.
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedTemplate(null);
                    setSignature('');
                    setAgreed(false);
                  }}
                  className="flex-1 btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={signing || !signature.trim() || !agreed}
                  className="flex-1 btn-teal flex items-center justify-center gap-2"
                  data-testid="submit-consent-btn"
                >
                  {signing ? <div className="spinner" /> : (
                    <>
                      <Check size={18} weight="bold" />
                      Sign Document
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Consent Modal */}
      {viewConsent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-slate-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    {viewConsent.consent_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Signed by {viewConsent.patient_name} on {new Date(viewConsent.signature_date).toLocaleString()}
                  </p>
                </div>
                <button onClick={() => setViewConsent(null)} className="text-slate-400 hover:text-slate-600">
                  <X size={24} />
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                {viewConsent.consent_text}
              </div>
              
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                  <CheckCircle size={18} weight="fill" />
                  Electronically Signed
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Signature:</span>
                    <p className="font-semibold text-slate-900 italic">{viewConsent.signature}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Date & Time:</span>
                    <p className="font-medium text-slate-900">
                      {new Date(viewConsent.signature_date).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-slate-200 flex-shrink-0">
              <button onClick={() => setViewConsent(null)} className="btn-outline w-full">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
